"use strict";

import { Application } from "../main/application.js";
import { Screen } from "../systems/core/screen.js";
import { UIObjectCreator } from "./controllers/uiObjectCreator.js";
import { UITransformHandler } from "./controllers/uiTransformHandler.js";
import { Vector2 } from "../math/vectors.js";

export class UIBitmapText extends PIXI.BitmapText {
    constructor(config) {
        super(config.text, config.fontProperties);

        this.name = config.name;

        // Create Parenting
        UIObjectCreator.createParenting(this, config);

        // Set Original Scale/Size/AspectRatio
        this._originalSize = { width: this.width, height: this.height };
        this._originalAspectRatio = this._originalSize.width / this._originalSize.height;

        // Create Rect
        UIObjectCreator.createRect(this, config);

        UIObjectCreator.setTextAspectRatioBehaviour(this, config);

        // Set Rotation Angle
        UIObjectCreator.setRotationAngle(this, config);
        // Set Visibility
        UIObjectCreator.setVisibility(this, config);
        // Set Renderability
        UIObjectCreator.setRenderability(this, config);
        // Set Interactibility
        UIObjectCreator.setInteractibility(this, config);

        UIObjectCreator.setResolutionListeners(this, false, config);
        UIObjectCreator.setOrientationListeners(this, false, config);

        UIObjectCreator.addGenericTransformations(this, config);

        this._autoWordWrap = config.wordWrap ?? false;
        this._lineCount = 1;

        config.onInitialize?.bind(this)(config);

        this._onTextChangedCallback = config.onTextChanged;

        this.updateNow();
    }

    get widthUI() {
        return this.parentContainer.transformFromUI(this.width);
    }
    get heightUI() {
        return this.parentContainer.transformFromUI(this.height);
    }
    
    get lossyScaleX() {
        return this.rect.scale.x * (this.parentContainer.lossyScaleX ?? 1);
    }
    get lossyScaleY() {
        return this.rect.scale.y * (this.parentContainer.lossyScaleY ?? 1);
    }
    get lossyScale() {
        return this.lossyScaleY;
    }

    get preferredBoundX() {
        return this.width * this.lossyScaleX;
    }
    get preferredBoundY() {
        return this.height * this.lossyScaleY;
    }
    get preferredBounds() {
        return { width: this.preferredBoundX, height: this.preferredBoundY };
    }

    dispose() {
        this.renderable = false;
        this.parentContainer?.removeChild(this);
        this.destroy();
    }

    // SetParent
    setParent(newParent) {
        this.parentContainer = newParent;
        this.parentContainer.addChild(this);
        this.updateNow();
    }

    setRect(newRect) {
        UITransformHandler.setRect(this, newRect);
        this.updateNow();
    }

    getPosition() {
        return { x: this.x, y: this.y };
    }
    setPosition(x,y) {
        this.x = x.x ?? x;
        this.y = x.y ?? y;
    }
    getRealWorldPosition() {
        this.parentContainer.updateTransform();
        return this.parentContainer.toGlobal(this.getPosition());
    }
    getWorldPosition(invertY) {
        let position = this.getRealWorldPosition();
        position.x -= Application.main.worldCenterX;
        position.y -= Application.main.worldCenterY;
        if (invertY) {
            position.y = -position.y;
        }
        return Vector2.divide(position, Screen.main.stageScale);
    }
    
    transformToLocal(position) {
        return this.toLocal(position);
    }

    _refreshTransform() {
        if (this.parentContainer === undefined) return;

        this.scale.set(1, 1);
        
        UITransformHandler.updateGraphicalTransform(this, {...this.parentContainer.preferredBounds});

        if (this._autoWordWrap) {
            this.maxWidth = this.width;
            this.text = text;
        }

        this.scale.set(
            this.transformToUI(this.rect.scale.x),
            this.transformToUI(this.rect.scale.y)
        );

        this.calculatedRect.size.width = this.transformFromUI(this.width)
        this.calculatedRect.size.height = this.transformFromUI(this.height);

        this.width *= this.rect.scale.x;
        this.height *= this.rect.scale.y;
    }

    onResolutionChanged() {
        this._onResolutionWillChangeCallback?.(this);

        this._refreshTransform();
        
        this._onResolutionChangedCallback?.(this);
    }

    onOrientationChanged() {
        this._onOrientationChangedCallback?.(this);
    }

    updateNow() {
        this.onResolutionChanged();
        this.recalculateLineCount();
    }

    setMaxWidth(width) {
        if (this._autoWordWrap) return;

        this.maxWidth = width;
        this.refreshText(false);
    }
    refreshText(forceCallback) {
        this.setText(this.text, forceCallback);
    }

    setText(text,forceCallback) {
        this.text = text;
        this.updateNow();

        if (forceCallback === undefined || forceCallback) {
            this._onTextChangedCallback?.();
        }
    }

    get lineCount() {
        return this._lineCount;
    }
    get hasExtraLines() {
        return this._lineCount > 1;
    }

    recalculateLineCount() {
        this._lineCount = Math.ceil(this.calculatedRect.size.height / this._maxLineHeight);
    }
}